import { gql } from "@apollo/client";
import { BigNumberish } from "ethers";
import { useWeb3React } from "@web3-react/core";
import { getGmxGraphClient } from "../../../../lib/subgraph";
import { useChainId } from "../../../../lib/chains";
import { bigNumberify } from "../../../../lib/numbers";
import useSWR from "swr";

type ContributionItem = {
  index: number;
  tier: number;
  paidAmount: BigNumberish | undefined;
  purchasedAmount: BigNumberish | undefined;
  rewardAmount: BigNumberish | undefined;
  isActive: boolean;
};

type ScheduleInfo = {
  plpAmount: string;
  paidAmount: string;
  allocated: string;
  index: number;
  isDestroyed: boolean;
  tier: number;
};

type VestInfo = {
  scheduleInfo: ScheduleInfo[];
};

export const useContributions = (): ContributionItem[] => {
  const { account, active } = useWeb3React();
  const { chainId } = useChainId();

  const fetcher = async ([, , account]) => {
    const query = gql(`
    query contributionInfoByUser($account: String!) {
      vestInfo(id: $account) {
        totalAllocated
        totalClaimed
        scheduleInfo {
          tier
          plpAmount
          paidAmount
          isDestroyed
          index
          destroyed
          claimed
          allocated
        }
        totalDestroyed
      }
    }`);

    const res = await getGmxGraphClient(chainId)?.query<{ vestInfo: VestInfo }>({
      query,
      variables: { account: (account || "").toLowerCase() },
      fetchPolicy: "network-only",
    });

    if (!res?.data.vestInfo) {
      return [];
    }

    return res.data.vestInfo.scheduleInfo.map((tierItem, index): ContributionItem => {
      return {
        index: tierItem.index,
        tier: tierItem.tier,
        paidAmount: bigNumberify(tierItem.paidAmount),
        purchasedAmount: bigNumberify(tierItem.plpAmount),
        rewardAmount: bigNumberify(tierItem.allocated),
        isActive: !tierItem.isDestroyed,
      };
    });
  };

  const { data: contributions = [] } = useSWR(
    [`LiquidityEvent:contributions-by-user:${active}:${account}`, chainId, account],
    fetcher,
    { refreshInterval: 60 * 1000 }
  );

  return contributions.sort((a, b) => (a.index > b.index ? 1 : 0));
};
